import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Description,
  FieldError,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectSection,
  SelectTrigger,
} from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { TabLayout } from "../TabLayout";

const schema = z.object({
  id: z.number(),
  results: z.string().min(1, "Required"),
});
type FormValues = z.infer<typeof schema>;

const placeholder = `Blank
01:01
01:02
02:01
02:02
...`;

type TestResultSpec = NonNullable<
  RouterOutputs["admin"]["testResultSpec"]["get"]
>;

type ResultOptions = { resultOptions?: string[] | null };

export function HLAResultsForm({ resultSpec }: { resultSpec: TestResultSpec }) {
  const update = trpc.admin.testResultSpec.update.useMutation();

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      id: resultSpec.id,
      results: (resultSpec.options as ResultOptions)?.resultOptions?.join("\n"),
    },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const resultOptions = values.results
      .split("\n")
      .map((r) => r.trim())
      .filter(Boolean);

    update.mutate(
      {
        id: values.id,
        patch: { options: { resultOptions } },
      },
      mutationResponse({
        errorTitle: "Error updating alleles",
        successMessage: "Alleles updated",
      }),
    );
  };

  return (
    <TabLayout
      description="These alleles will be available for result entry. Add one allele per
    line."
      actions={<CopyFlyout onCopy={(value) => setValue("results", value)} />}
    >
      <div className="flex gap-4">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <Controller
              control={control}
              name="results"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="allele-entry"
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Alleles</Label>
                  <TextArea
                    rows={20}
                    placeholder={placeholder}
                    className="max-w-[400px]"
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <div className="flex gap-2">
              <Button type="submit" isPending={update.isPending}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </TabLayout>
  );
}

function CopyFlyout({ onCopy }: { onCopy: (values: string) => void }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option | null>(null);
  const testSpecs = trpc.tests.all.useQuery({ type: "HLA" });
  const resultSpecs = trpc.admin.testResultSpec.list.useQuery();

  const options = (testSpecs.data || []).map((ts) => ({
    label: `${ts.name} (${ts.code})`,
    options: (resultSpecs.data || [])
      .filter((rs) => rs.testId === ts.id)
      .map((rs) => ({
        label: rs.name,
        value: rs.id,
        disabled: true,
        testName: ts.name,
        testCode: ts.code,
        options: rs.options,
      })),
  }));

  const selectedOptions = (
    (selected?.options as ResultOptions)?.resultOptions || []
  ).join("\n");

  // biome-ignore lint/correctness/useExhaustiveDependencies: update on open change
  useEffect(() => {
    setSelected(null);
  }, [open]);

  const onCopyClicked = () => {
    setOpen(false);
    onCopy(selectedOptions);
  };

  type Option = (typeof options)[0]["options"][0];

  return (
    <DialogTrigger>
      <Button onPress={() => setOpen(true)} variant="secondary">
        Copy results
      </Button>
      <Sheet isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Copy result options</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-4">
              <Text>
                Select a test result spec below to copy its configured alleles.
                This action copies the results into the text box to the left.
                Save must still be clicked to save changes.
              </Text>
              <Select
                onSelectionChange={(key) => {
                  const option = options
                    .flatMap((o) => o.options)
                    .find((o) => o.value === key);
                  if (option) {
                    setSelected(option);
                  }
                }}
              >
                <Label>Select test result spec</Label>
                <SelectTrigger />
                <SelectList items={options}>
                  {(item) => (
                    <SelectSection
                      title={item.label}
                      items={item.options}
                      id={item.label}
                    >
                      {(option) => (
                        <SelectItem
                          id={option.value ?? ""}
                          textValue={`${option.testName} (${option.testCode}) ${option.label}`}
                        >
                          {option.label}
                        </SelectItem>
                      )}
                    </SelectSection>
                  )}
                </SelectList>
              </Select>
              {selected && (
                <TextField isReadOnly value={selectedOptions}>
                  <Label>Preview</Label>
                  <TextArea rows={10} />
                  <Description>
                    This is a preview of the options that will be copied.
                  </Description>
                </TextField>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onPress={onCopyClicked}>Copy</Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Sheet>
    </DialogTrigger>
  );
}
